import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Calendar, Repeat, Clock, TrendingUp, Building2,
  ExternalLink, Sparkles, ChevronDown, BarChart3, Target, Activity,
} from 'lucide-react';
import Sparkline from '../components/ui/Sparkline';

/** "2026-06-10" -> "Jun 2026" */
function monthLabel(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Collapse a dated event list into one point per month, gaps included. */
function monthlyFromTimeline(timeline) {
  const counts = new Map();
  for (const t of timeline) {
    const key = String(t.date).slice(0, 7); // YYYY-MM
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const keys = Array.from(counts.keys()).sort();
  if (keys.length === 0) return [];

  // Fill the empty months so a quiet spell shows as a trough, not a straight line.
  const out = [];
  const [startY, startM] = keys[0].split('-').map(Number);
  const [endY, endM] = keys[keys.length - 1].split('-').map(Number);
  for (let y = startY, m = startM; y < endY || (y === endY && m <= endM);) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    out.push({ month: key, count: counts.get(key) || 0 });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}
import { getClinicByName } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { humanizeInterval, relativeDays, formatDate } from '../utils/format';
import StatTile from '../components/ui/StatTile';
import SectionHeader from '../components/ui/SectionHeader';
import CadenceBar from '../components/ui/CadenceBar';
import { SkeletonCard, Skeleton } from '../components/ui/Skeleton';

function RhythmBadge({ rhythm }) {
  if (!rhythm) return null;
  const tone = rhythm.isRegular
    ? 'bg-accent-100 text-accent-700'
    : rhythm.label === 'One-off'
      ? 'bg-ink-100 text-ink-500'
      : 'bg-amber-100 text-amber-800';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${tone}`}>
      <Repeat className="w-3 h-3" /> {rhythm.label}
    </span>
  );
}

function LocationCard({ loc }) {
  const [open, setOpen] = useState(false);
  const dates = loc.events.map((e) => e.startDate).filter(Boolean);
  const next = loc.predictedNext;

  return (
    <div className="card card-hover">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/locations/${encodeURIComponent(loc.location)}`}
            className="inline-flex items-center gap-1.5 font-semibold text-ink-900 hover:text-dental-700"
          >
            <MapPin className="w-4 h-4 text-dental-600" /> {loc.location}
          </Link>
          <p className="text-sm text-ink-500 mt-0.5">
            Visited <span className="font-semibold text-ink-700">{loc.visitCount}×</span>
            {loc.avgIntervalDays != null && <> · {humanizeInterval(loc.avgIntervalDays)}</>}
            {loc.daysSinceLast != null && <> · last {relativeDays(loc.daysSinceLast)}</>}
          </p>
        </div>
        <RhythmBadge rhythm={loc.rhythm} />
      </div>

      <CadenceBar dates={dates} predictedNext={next} />

      {next && (
        <div className="flex items-center gap-2 text-sm rounded-xl bg-accent-50 ring-1 ring-accent-100 px-3 py-2">
          <Sparkles className="w-4 h-4 text-accent-600 shrink-0" />
          <span className="text-accent-800">
            Likely next around <span className="font-semibold">{formatDate(next.date)}</span>
            {next.daysUntil >= 0 ? ` (${relativeDays(-next.daysUntil)})` : ' (overdue)'}
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-wide text-accent-500 font-semibold">
            {next.confidence} conf.
          </span>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink-500 hover:text-ink-700"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        {open ? 'Hide' : 'Show'} {loc.events.length} event{loc.events.length === 1 ? '' : 's'}
      </button>

      {open && (
        <ul className="mt-2 space-y-2">
          {loc.events.map((e) => (
            <li key={e.id} className="flex items-start gap-3 text-sm border-t border-ink-100 pt-2">
              <Calendar className="w-4 h-4 text-ink-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink-800 truncate">{e.title || 'Untitled event'}</p>
                <p className="text-xs text-ink-500">
                  {formatDate(e.startDate)}{e.offer ? ` · ${e.offer}` : ''}
                  {e.adCount > 1 ? ` · ${e.adCount} ads` : ''}
                </p>
              </div>
              {e.adUrl && (
                <a href={e.adUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 shrink-0">
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClinicProfile() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getClinicByName(name)
      .then((res) => { if (active) { setData(res.data.data); setError(null); document.title = `${res.data.data.clinicName} | Dental Events`; } })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load clinic')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; document.title = 'Dental Events'; };
  }, [name]);

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <Link to="/clinics" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-800">
        <ArrowLeft className="w-4 h-4" /> All clinics
      </Link>

      {loading ? (
        <>
          <Skeleton className="h-10 w-2/3" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
          <SkeletonCard />
          <SkeletonCard />
        </>
      ) : error ? (
        <div className="card bg-red-50 ring-red-200"><p className="text-red-700">{error}</p></div>
      ) : data ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-dental-600 text-white flex items-center justify-center text-xl font-bold shrink-0">
              {data.clinicName?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="page-title truncate">{data.clinicName}</h1>
              <p className="text-sm text-ink-500">
                Active in {data.summary.locationCount} location{data.summary.locationCount === 1 ? '' : 's'}
                {data.summary.isActiveNow && <span className="ml-2 text-green-600 font-medium">● running ads now</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/ad-stats?clinic=${encodeURIComponent(data.clinicName || '')}`}
                className="btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap"
              >
                <BarChart3 className="w-4 h-4" />
                Ad statistics
              </Link>
              <Link
                to={`/targeting?clinic=${encodeURIComponent(data.clinicName || '')}`}
                className="btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap"
              >
                <Target className="w-4 h-4" />
                Targeting
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatTile icon={Calendar} label="Total events" value={data.summary.totalEvents} tone="dental" />
            <StatTile icon={MapPin} label="Locations" value={data.summary.locationCount} tone="accent" />
            <StatTile icon={Clock} label="Avg interval" value={humanizeInterval(data.summary.avgIntervalDays)} tone="blue" />
            <StatTile icon={TrendingUp} label="Last active" value={relativeDays(data.summary.daysSinceLast)} subValue={formatDate(data.summary.lastActive)} tone="green" />
          </div>

          {data.treatmentTypes?.length > 0 && (
            <div className="card">
              <SectionHeader icon={Sparkles} title="Treatment focus" />
              <div className="flex flex-wrap gap-2">
                {data.treatmentTypes.map((t) => (
                  <span key={t.treatment} className="chip capitalize">
                    {t.treatment} <span className="text-ink-400">· {t.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <SectionHeader
              icon={MapPin}
              title="Where they go & how often"
              subtitle="Each place this clinic has run events, with the rhythm and the likely next visit"
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {data.locations.map((loc) => <LocationCard key={loc.location} loc={loc} />)}
            </div>
          </div>

          {/* Activity over time. The API has always returned this timeline;
              nothing rendered it until now. Grouped by month so a run of
              events in one month reads as a peak rather than a row of 1s. */}
          {data.timeline?.length > 0 && (
            <div className="card">
              <SectionHeader
                icon={Activity}
                title="Activity over time"
                subtitle={`${data.timeline.length} dated event${data.timeline.length === 1 ? '' : 's'}, by month`}
              />
              <Sparkline data={monthlyFromTimeline(data.timeline)} dataKey="count" height={64} />
              <div className="flex justify-between text-xs text-ink-400 mt-1">
                <span>{monthLabel(data.timeline[0].date)}</span>
                <span>{monthLabel(data.timeline[data.timeline.length - 1].date)}</span>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default ClinicProfile;
